/**
 * Generic on-demand text translation via server LLM (llm-extract / Gemini).
 * Used by agent chat, canvas documents, and job postings.
 */
import { invokeLlmExtract } from './serverLlmService';

const CACHE_PREFIX = 'ggj_content_tr_v1_';
const MAX_CHUNK_CHARS = 10_000;

export const LOCALE_NAMES: Record<string, string> = {
  es: 'Spanish',
  pt: 'Brazilian Portuguese',
  ru: 'Russian',
};

const memoryCache = new Map<string, string>();

/** Serialize server LLM calls to avoid rate limits when many cards translate at once. */
let llmQueue: Promise<unknown> = Promise.resolve();
const LLM_MIN_INTERVAL_MS = 1_500;

function enqueueLlm<T>(fn: () => Promise<T>): Promise<T> {
  const run = llmQueue.then(() => fn());
  llmQueue = run
    .catch(() => {})
    .then(() => new Promise((resolve) => setTimeout(resolve, LLM_MIN_INTERVAL_MS)));
  return run;
}

function hashKey(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

function readCache(cacheId: string, locale: string, text: string): string | null {
  const key = `${cacheId}:${locale}:${hashKey(text)}`;
  const mem = memoryCache.get(key);
  if (mem) return mem;
  try {
    const raw = sessionStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (raw) {
      memoryCache.set(key, raw);
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeCache(cacheId: string, locale: string, source: string, translated: string): void {
  const key = `${cacheId}:${locale}:${hashKey(source)}`;
  memoryCache.set(key, translated);
  try {
    sessionStorage.setItem(`${CACHE_PREFIX}${key}`, translated);
  } catch {
    // quota
  }
}

function alreadyLocalized(text: string, normalized: string): boolean {
  if (normalized === 'ru') return /[а-яёА-ЯЁ]/.test(text);
  if (normalized === 'es') return /[áéíóúñ¿¡]/i.test(text) || /\b(para|con|los|las|una)\b/i.test(text);
  if (normalized === 'pt') return /[ãõçáéíóú]/i.test(text) || /\b(para|com|uma|você)\b/i.test(text);
  return false;
}

export function normalizeContentLocale(locale: string): string {
  return locale.split('-')[0]?.toLowerCase() || 'en';
}

export { alreadyLocalized };

/** Map i18next language code to ai-companion response_language. */
export function resolveResponseLanguage(locale?: string): string {
  const code = normalizeContentLocale(locale ?? 'en');
  if (code === 'pt') return 'pt';
  if (code === 'ru') return 'ru';
  if (code === 'es') return 'es';
  return 'en';
}

async function callServerLlmOnce(
  texts: string[],
  targetLanguage: string,
  context: string,
): Promise<string[]> {
  const content = await invokeLlmExtract({
    prompt:
      `You translate ${context} from English to ${targetLanguage}. ` +
      'Preserve markdown structure, bullet lists, headings, and placeholders. ' +
      'Keep person names, company names, URLs, email addresses, and technical terms when appropriate. ' +
      'Return JSON: {"translations":["...", "..."]} with the same number of items in the same order.',
    text: JSON.stringify({ strings: texts }),
    jsonMode: true,
    temperature: 0.15,
    maxTokens: 4000,
  });

  const parsed = JSON.parse(content) as { translations?: unknown };
  if (!Array.isArray(parsed.translations) || parsed.translations.length !== texts.length) {
    throw new Error('Translation count mismatch from server LLM');
  }

  return parsed.translations.map((s) => (typeof s === 'string' ? s : String(s ?? '')));
}

async function callServerLlm(
  texts: string[],
  targetLanguage: string,
  context: string,
): Promise<string[]> {
  return enqueueLlm(async () => {
    try {
      return await callServerLlmOnce(texts, targetLanguage, context);
    } catch (err) {
      const msg = (err as Error).message ?? '';
      if (/429|rate|overloaded/i.test(msg)) {
        await new Promise((r) => setTimeout(r, 3_500));
        return callServerLlmOnce(texts, targetLanguage, context);
      }
      throw err;
    }
  });
}

/**
 * Translate one or more text blocks. Returns originals on `en` or failure.
 */
export async function translateTextBlocks(
  cacheId: string,
  locale: string,
  texts: string[],
  context = 'UI and document content',
  signal?: AbortSignal,
): Promise<string[]> {
  void signal;
  const normalized = normalizeContentLocale(locale);
  if (normalized === 'en' || texts.length === 0) return texts;

  const targetLanguage = LOCALE_NAMES[normalized];
  if (!targetLanguage) return texts;

  const result: string[] = [];
  const pending: Array<{ index: number; text: string }> = [];

  texts.forEach((text, index) => {
    const trimmed = text?.trim() ?? '';
    if (!trimmed) {
      result[index] = text;
      return;
    }
    const cached = readCache(cacheId, normalized, trimmed);
    if (cached) {
      result[index] = cached;
    } else {
      pending.push({
        index,
        text: trimmed.length > MAX_CHUNK_CHARS ? `${trimmed.slice(0, MAX_CHUNK_CHARS)}\n\n…` : trimmed,
      });
    }
  });

  if (pending.length === 0) return texts.map((t, i) => result[i] ?? t);

  try {
    const translated = await callServerLlm(
      pending.map((p) => p.text),
      targetLanguage,
      context,
    );
    pending.forEach((p, i) => {
      const value = translated[i];
      if (!value) return;
      writeCache(cacheId, normalized, p.text, value);
      result[p.index] = value;
    });
  } catch {
    return texts;
  }

  return texts.map((t, i) => result[i] ?? t);
}

export async function translateText(
  cacheId: string,
  locale: string,
  text: string,
  context?: string,
  signal?: AbortSignal,
): Promise<string> {
  const [out] = await translateTextBlocks(cacheId, locale, [text], context, signal);
  return out ?? text;
}
